import { useNavigate } from 'react-router-dom'
import { Check, X, ArrowLeft, Mail } from 'lucide-react'
import { PageHeader } from '../components/ui/PageHeader'
import { useTenant } from '../hooks/useTenant'

// ---------------------------------------------------------------------------
// Dati piani
// ---------------------------------------------------------------------------

const PLANS = [
  {
    key: 'trial',
    label: 'Trial',
    price: 'Gratuito',
    period: '',
    description: '14 giorni per esplorare la piattaforma.',
    color: 'var(--km-text-muted)',
    highlight: false,
  },
  {
    key: 'starter',
    label: 'Starter',
    price: '€ 149',
    period: '/ mese',
    description: 'Per PMI che iniziano a strutturare il controllo di gestione.',
    color: 'var(--km-primary)',
    highlight: false,
  },
  {
    key: 'growth',
    label: 'Growth',
    price: '€ 349',
    period: '/ mese',
    description: 'Per aziende con più fonti dati e team operativo.',
    color: 'var(--km-success)',
    highlight: true,
  },
  {
    key: 'enterprise',
    label: 'Enterprise',
    price: 'Su misura',
    period: '',
    description: 'Per gruppi con esigenze avanzate e SLA garantito.',
    color: '#a78bfa',
    highlight: false,
  },
]

const FEATURES: { label: string; plans: Record<string, string | boolean> }[] = [
  {
    label: 'Upload file / mese',
    plans: { trial: '3', starter: '20', growth: '100', enterprise: '∞' },
  },
  {
    label: 'Chiamate AI / mese',
    plans: { trial: '50', starter: '200', growth: '1.000', enterprise: '∞' },
  },
  {
    label: 'Connettori cloud attivi',
    plans: { trial: '∞', starter: '1', growth: '5', enterprise: '∞' },
  },
  {
    label: 'Dashboard BI (5 sezioni)',
    plans: { trial: true, starter: true, growth: true, enterprise: true },
  },
  {
    label: 'AI Narrative + Chat',
    plans: { trial: false, starter: true, growth: true, enterprise: true },
  },
  {
    label: 'Export Excel / PDF',
    plans: { trial: false, starter: true, growth: true, enterprise: true },
  },
  {
    label: 'Connettori avanzati (HubSpot, FIC)',
    plans: { trial: false, starter: false, growth: true, enterprise: true },
  },
  {
    label: 'Collaboratori con viste parziali',
    plans: { trial: false, starter: false, growth: true, enterprise: true },
  },
  {
    label: 'Dashboard sharing read-only',
    plans: { trial: false, starter: false, growth: true, enterprise: true },
  },
  {
    label: 'Business Review board-ready',
    plans: { trial: false, starter: false, growth: false, enterprise: true },
  },
  {
    label: 'SLA + supporto dedicato',
    plans: { trial: false, starter: false, growth: false, enterprise: true },
  },
]

// ---------------------------------------------------------------------------
// Componenti
// ---------------------------------------------------------------------------

function FeatureCell({ value }: { value: string | boolean }) {
  if (value === true)
    return <Check size={16} style={{ color: 'var(--km-success)', margin: '0 auto' }} />
  if (value === false)
    return <X size={16} style={{ color: 'var(--km-border)', margin: '0 auto' }} />
  return (
    <span className="text-sm font-medium" style={{ color: 'var(--km-text-primary)' }}>
      {value}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Pagina
// ---------------------------------------------------------------------------

export function UpgradePage() {
  const navigate = useNavigate()
  const { data: tenant } = useTenant()
  const currentPlan = tenant?.plan ?? 'trial'

  return (
    <div className="space-y-10 max-w-5xl">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm transition-opacity hover:opacity-70"
          style={{ color: 'var(--km-text-muted)' }}
        >
          <ArrowLeft size={15} /> Indietro
        </button>
      </div>

      <PageHeader
        title="Scegli il piano giusto"
        subtitle="Tutte le funzionalità di Koremargin, adatte alla dimensione della tua azienda."
      />

      {/* Card piani */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PLANS.map((plan) => {
          const isCurrent = plan.key === currentPlan
          return (
            <div
              key={plan.key}
              className="rounded-xl border p-5 flex flex-col gap-3 relative"
              style={{
                background: plan.highlight ? 'rgba(99,102,241,0.07)' : 'var(--km-surface)',
                borderColor: plan.highlight ? 'var(--km-primary)' : 'var(--km-border)',
              }}
            >
              {plan.highlight && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-widest px-3 py-0.5 rounded-full"
                  style={{ background: 'var(--km-primary)', color: '#fff' }}
                >
                  Più scelto
                </div>
              )}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: plan.color }}>
                  {plan.label}
                </p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold" style={{ color: 'var(--km-text-primary)' }}>
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className="text-xs" style={{ color: 'var(--km-text-muted)' }}>
                      {plan.period}
                    </span>
                  )}
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--km-text-muted)' }}>
                  {plan.description}
                </p>
              </div>

              {isCurrent ? (
                <div
                  className="mt-auto text-center text-xs font-semibold py-2 rounded-lg"
                  style={{ background: 'var(--km-surface-2)', color: 'var(--km-text-muted)' }}
                >
                  Piano attuale
                </div>
              ) : plan.key === 'enterprise' ? (
                <a
                  href="mailto:sales@koremargin.com?subject=Richiesta piano Enterprise"
                  className="mt-auto flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg transition-opacity hover:opacity-80"
                  style={{ background: 'var(--km-surface-2)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.3)' }}
                >
                  <Mail size={13} /> Contattaci
                </a>
              ) : (
                <a
                  href={`mailto:sales@koremargin.com?subject=Upgrade a piano ${plan.label}&body=Ciao, vorrei passare al piano ${plan.label}.`}
                  className="mt-auto text-center text-xs font-semibold py-2 rounded-lg transition-opacity hover:opacity-80"
                  style={{ background: 'var(--km-gradient-brand)', color: '#fff' }}
                >
                  Passa a {plan.label}
                </a>
              )}
            </div>
          )
        })}
      </div>

      {/* Tabella feature */}
      <div
        className="rounded-xl border overflow-x-auto"
        style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
      >
        <table className="w-full text-sm min-w-[600px]">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--km-border)', background: 'var(--km-surface-2)' }}>
              <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--km-text-muted)' }}>
                Funzionalità
              </th>
              {PLANS.map((p) => (
                <th
                  key={p.key}
                  className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider"
                  style={{ color: p.key === currentPlan ? p.color : 'var(--km-text-muted)' }}
                >
                  {p.label}
                  {p.key === currentPlan && (
                    <span className="ml-1 text-[9px] opacity-70">(attuale)</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FEATURES.map((f, i) => (
              <tr
                key={f.label}
                style={{ borderBottom: i < FEATURES.length - 1 ? '1px solid var(--km-border)' : undefined }}
              >
                <td className="px-5 py-3" style={{ color: 'var(--km-text-secondary)' }}>
                  {f.label}
                </td>
                {PLANS.map((p) => (
                  <td key={p.key} className="px-4 py-3 text-center">
                    <FeatureCell value={f.plans[p.key]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-center" style={{ color: 'var(--km-text-muted)' }}>
        Prezzi IVA esclusa. Per piani annuali o personalizzati scrivere a{' '}
        <a href="mailto:sales@koremargin.com" className="underline" style={{ color: 'var(--km-primary)' }}>
          sales@koremargin.com
        </a>
      </p>
    </div>
  )
}
